/** Grid strategies (docs/14-grid-bots.md): one coin, a price range cut into levels, a buy resting below the price at
 *  every level and a sell above it; each time price swings between two neighbouring levels the grid earns the gap.
 *  Long grids hold the coin, short grids are short it, neutral grids (futures) start flat and lean against moves. */
import { z } from "zod";
import { TF_MS, type Timeframe } from "./indicators.ts";
import { RULES_FEES } from "./rules.ts";

const Pct = (min: number, max: number) => z.number().min(min).max(max);

export const GridSpec = z.object({
  kind: z.literal("grid"),
  name: z.string().min(1).max(60),
  about: z.string().max(400).optional(),
  market: z.enum(["spot", "futures"]),
  venue: z.literal("binance").default("binance"),
  symbol: z.string().min(2),                                       // e.g. "ETHUSDT" (futures 1000x coins as "PEPEUSDT:1000PEPEUSDT")
  timeframe: z.enum(["1m", "5m", "15m"]).default("5m"),            // bar resolution used to work the orders (finer = more swings seen)
  mode: z.enum(["long", "short", "neutral"]).default("long"),
  range: z.discriminatedUnion("type", [
    z.object({ type: z.literal("pct"), below: Pct(0.5, 90), above: Pct(0.5, 500) }).strict(),   // around the price when the grid starts
    z.object({ type: z.literal("price"), lower: z.number().positive(), upper: z.number().positive() }).strict(),
  ]),
  levels: z.number().int().min(3).max(200),                         // grid lines; levels − 1 cells
  spacing: z.enum(["geometric", "arithmetic"]).default("geometric"),
  investment: z.object({ mode: z.enum(["equityPct", "fixed"]).default("equityPct"), value: z.number().positive() }).strict(), // margin (futures) or cash (spot) the grid uses
  leverage: z.number().min(1).max(20).default(1),
  outOfRange: z.object({
    above: z.enum(["wait", "follow", "stop"]).default("wait"),      // follow: rebuild the grid around the new price
    below: z.enum(["wait", "follow", "stop"]).default("wait"),
  }).strict().default({}),
  stop: z.object({
    takeProfitPct: Pct(1, 1000).optional(),                         // close everything once the grid is up this much on its investment
    stopLossPct: Pct(1, 100).optional(),                            // … or down this much
    belowPct: Pct(0.1, 90).optional(),                              // … or price falls this far below the range
    abovePct: Pct(0.1, 500).optional(),                             // … or rises this far above it
  }).strict().default({}),
  safety: z.object({ stopAt: Pct(5, 90).default(50) }).strict().default({}),
}).strict();
export type GridSpec = z.infer<typeof GridSpec>;
export type GridSpecInput = z.input<typeof GridSpec>;

/** Grid lines for a range (ascending). */
export function gridLevels(lower: number, upper: number, n: number, spacing: GridSpec["spacing"]): number[] {
  return Array.from({ length: n }, (_, i) => spacing === "geometric" ? lower * (upper / lower) ** (i / (n - 1)) : lower + ((upper - lower) * i) / (n - 1));
}
/** Range around a start price. */
export function gridRange(spec: GridSpec, price: number): [number, number] {
  const r = spec.range;
  return r.type === "price" ? [r.lower, r.upper] : [price * (1 - r.below / 100), price * (1 + r.above / 100)];
}
/** Profit of one swing across one cell as a fraction (geometric: the same for every cell; arithmetic: the narrowest). */
export function cellStep(spec: GridSpec, price = 100): number {
  const [lo, hi] = gridRange(spec, price), cells = spec.levels - 1;
  return spec.spacing === "geometric" ? (hi / lo) ** (1 / cells) - 1 : (hi - lo) / cells / hi;
}

export interface GridIssue { level: "error" | "warning"; field: string; message: string }
export function validateGrid(spec: GridSpec): GridIssue[] {
  const out: GridIssue[] = [];
  const e = (field: string, message: string) => out.push({ level: "error", field, message });
  const w = (field: string, message: string) => out.push({ level: "warning", field, message });
  if (spec.market === "spot" && spec.mode !== "long") e("mode", "Spot grids can only be long (they can't sell what they don't hold).");
  if (spec.market === "spot" && spec.leverage !== 1) e("leverage", "Spot can't borrow: leverage must be 1×.");
  if (spec.range.type === "price" && spec.range.lower >= spec.range.upper) e("range", "The lower price must be below the upper price.");
  const step = cellStep(spec), fee = RULES_FEES[spec.market].maker;
  if (step <= 2 * fee) e("levels", `Each grid step is ${(step * 100).toFixed(3)}%, less than the ${(2 * fee * 100).toFixed(2)}% in fees a buy and a sell cost: every swing would lose money. Use fewer levels or a wider range.`);
  else if (step < 4 * fee) w("levels", `Each grid step is ${(step * 100).toFixed(2)}%; fees take half or more of every swing.`);
  if (spec.investment.mode === "fixed" && (spec.investment.value * spec.leverage) / (spec.levels - 1) < 5) e("investment", `$${spec.investment.value} over ${spec.levels - 1} cells is under Binance's $5 minimum per order.`);
  if (spec.range.type === "price") w("range", "A fixed price range only fits the market around today's price; backtests over years spend most of their time outside it. A range around the start price (%) tests fairly.");
  if (spec.market === "futures" && spec.mode !== "short" && spec.range.type === "pct" && spec.leverage > 1 && spec.range.below >= 100 / spec.leverage * 0.9)
    w("leverage", `At ${spec.leverage}× a fall to the bottom of the range (−${spec.range.below}%) comes close to liquidation.`);
  if (spec.outOfRange.below === "wait" && spec.mode !== "short" && !spec.stop.belowPct && !spec.stop.stopLossPct)
    w("stop", "Nothing limits the loss if price falls through the range: the grid keeps holding everything it bought on the way down.");
  if (spec.timeframe !== "1m") w("timeframe", `Orders are worked on ${spec.timeframe} bars, which see at most one swing down and one up per bar, so backtests undercount the swings a live grid catches. Results lean conservative.`);
  return out;
}

export const GRID_MAX_BARS = 6_000_000;
export function gridTooBig(spec: GridSpec, from: number, to: number): string | null {
  const n = Math.ceil((to - from) / TF_MS[spec.timeframe as Timeframe]);
  if (n <= GRID_MAX_BARS) return null;
  return `That's ${(n / 1e6).toFixed(1)}M ${spec.timeframe} bars; test at most ${Math.floor((GRID_MAX_BARS * TF_MS[spec.timeframe as Timeframe]) / 86_400_000)} days on ${spec.timeframe} bars.`;
}

const coin = (s: string) => s.split(":")[0]!.replace(/USDT$/, "");
const ACT: Record<string, string> = { wait: "waits for price to come back", follow: "moves the grid to the new price", stop: "closes everything and stops" };
export function describeGrid(s: GridSpec): string[] {
  const r = s.range, cells = s.levels - 1, step = cellStep(s);
  const out = [
    `${s.market === "spot" ? "Spot" : "Futures"} ${s.mode} grid on ${coin(s.symbol)}, ${s.levels} levels ${r.type === "pct" ? `from ${r.below}% below to ${r.above}% above the start price` : `from ${r.lower} to ${r.upper}`} (${s.spacing}, ${(step * 100).toFixed(2)}% apart).`,
    `${s.investment.mode === "fixed" ? `$${s.investment.value.toLocaleString("en-US")}` : `${s.investment.value}% of the account`} split over ${cells} cells${s.leverage > 1 ? ` at ${s.leverage}× leverage` : ""}: buys at each level below the price and sells one level up, earning the gap on every swing.`,
    s.mode === "long" ? "Starts by buying the coin for every level above the price, so it holds more as price falls and less as it rises."
      : s.mode === "short" ? "Starts short the coin for every level below the price, so it is shorter as price rises and covers as it falls."
      : "Starts flat: goes long as price falls and short as it rises, betting it comes back to the middle.",
    `Above the range it ${ACT[s.outOfRange.above]}; below the range it ${ACT[s.outOfRange.below]}.`,
  ];
  const st = s.stop, stops: string[] = [];
  if (st.takeProfitPct) stops.push(`takes profit at +${st.takeProfitPct}% on the investment`);
  if (st.stopLossPct) stops.push(`stops at −${st.stopLossPct}%`);
  if (st.belowPct) stops.push(`stops if price falls ${st.belowPct}% below the range`);
  if (st.abovePct) stops.push(`stops if price rises ${st.abovePct}% above the range`);
  if (stops.length) out.push(`Closes everything and ${stops.join(", ")}.`);
  out.push(`Orders are worked on ${s.timeframe} bars.`);
  return out;
}
